
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'reset_password'>('loading');
  const [message, setMessage] = useState('Se procesează...');
  const [newPassword, setNewPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');
        const type = hashParams.get('type');
        
        if (error) {
          setStatus('error');
          if (error === 'otp_expired') {
            setMessage('Link-ul de confirmare a expirat. Te rugăm să ceri altul.');
          } else {
            setMessage(errorDescription || 'A apărut o eroare.');
          }
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        // Dacă e link de resetare parolă
        if (type === 'recovery') {
          setStatus('reset_password');
          setMessage('Introdu noua parolă pentru contul tău.');
          return;
        }

        // Altfel e confirmare email / login normal
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;

        if (session) {
          setStatus('success');
          setMessage('Cont confirmat cu succes! Redirecționare către dashboard...');
          setTimeout(() => navigate('/dashboard'), 2000);
        } else {
          setStatus('error');
          setMessage('Nu am putut confirma contul. Te rugăm să te autentifici.');
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (err: any) {
        setStatus('error');
        setMessage(err.message || 'Eroare la procesare.');
        setTimeout(() => navigate('/login'), 5000);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setMessage("Parola trebuie să aibă minim 6 caractere.");
      return;
    }
    
    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      
      setStatus('success');
      setMessage('Parola a fost actualizată cu succes! Redirecționare...');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err: any) {
      setStatus('error');
      setMessage(err.message || 'Eroare la actualizarea parolei.');
      setTimeout(() => navigate('/login'), 4000);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
      {/* Background Aurora */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-indigo-600/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[60%] h-[60%] bg-blue-600/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="relative z-10 text-center max-w-md">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 border-4 border-indigo-600/20 border-t-indigo-600 rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-2xl font-black text-white italic mb-2">Confirmare Email</h2>
            <p className="text-indigo-400 text-sm">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">✓</span>
            </div>
            <h2 className="text-2xl font-black text-white italic mb-2">Succes!</h2>
            <p className="text-green-400 text-sm">{message}</p>
          </>
        )}

        {status === 'reset_password' && (
          <form onSubmit={handleUpdatePassword} className="bg-white/5 backdrop-blur-3xl p-10 rounded-[3.5rem] border border-white/10 text-left">
            <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">🔐</span>
            </div>
            <h2 className="text-2xl font-black text-white italic mb-2 text-center">Resetare Parolă</h2>
            <p className="text-indigo-400 text-[10px] text-center mb-8 uppercase tracking-widest">{message}</p>
            
            <div className="space-y-2 mb-6">
              <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Noua Parolă</label>
              <input 
                type="password" 
                required 
                value={newPassword} 
                onChange={(e) => setNewPassword(e.target.value)} 
                className="w-full p-5 bg-[#0f172a] border border-white/5 rounded-2xl outline-none font-bold text-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all placeholder:text-slate-700" 
                placeholder="Minim 6 caractere"
              />
            </div>
            
            <button 
              type="submit" 
              disabled={isUpdatingPassword}
              className="w-full py-6 bg-indigo-600 text-white rounded-2xl font-black text-[12px] uppercase tracking-[0.3em] shadow-2xl hover:bg-indigo-700 transition-all disabled:opacity-50"
            >
              {isUpdatingPassword ? "SE ACTUALIZEAZĂ..." : "SALVEAZĂ PAROLA"}
            </button>
          </form>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">✗</span>
            </div>
            <h2 className="text-2xl font-black text-white italic mb-2">Eroare</h2>
            <p className="text-rose-400 text-sm mb-4">{message}</p>
            <button 
              onClick={() => navigate('/register')} 
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-indigo-700 transition-all"
            >
              Înapoi la Înregistrare
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
